/* eslint-disable no-bitwise */
import {
    ChangeDetectorRef,
    Component,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { forkJoin } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ProduktinstruktionValidationScope } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { IProduktinstruktion } from "../models/IProduktdefinition";
import { MassediteraProduktdefinitionDto } from "../models/MassediteraProduktdefinitionDto";
import { MassediteraProduktinstruktionerResponseDto } from "../models/MassediteraProduktinstruktionerResponseDto";
import { IProduktdefinitionBasdata } from "../models/ProduktdefinitionModels";
import { ProduktdefinitionViewModel } from "../models/ProduktdefinitionViewModel";
import { ValidationMessage } from "../models/ValidationMessage";
import { ProduktinstruktionerValidation } from "../produktinstruktioner-details/produktinstruktioner.validation";
import { FordelningsmatrisComponent } from "../produktinstruktioner-details/tabs/fordelningsmatris/fordelningsmatris.component";
import { VardematrisComponent } from "../produktinstruktioner-details/tabs/vardematris/vardematris.component";
import { ProduktinstruktionService } from "../services/produktinstruktion.service";
import { ProduktinstruktionerMasseditHeaderComponent } from "./produktinstruktioner-massedit-header/produktinstruktioner-massedit-header.component";

@Component({
    selector: "rask-produktinstruktioner-massedit",
    templateUrl: "./produktinstruktioner-massedit.component.html",
    styleUrls: ["./produktinstruktioner-massedit.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        PageComponent,
        ProduktinstruktionerMasseditHeaderComponent,
        BlockComponent,
        TabsComponent,
        TabComponent,
        VardematrisComponent,
        FordelningsmatrisComponent,
        SaveButtonComponent,
        ButtonComponent
    ]
})
export class ProduktinstruktionerMassEditComponent implements OnInit {

    puids: string[];
    form = new UntypedFormGroup({});
    produktinstruktionBasdata: IProduktdefinitionBasdata;
    aktivProduktdefinition: ProduktdefinitionViewModel;
    validationContext: IValidationContext;
    validation: IValidationScope;
    ovrigaProduktinstruktioner: ProduktdefinitionViewModel[] = [];

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly produktinstruktionService = inject(ProduktinstruktionService);
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly formBinder = inject(FormBinderService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly toastService = inject(ToastService);
    private readonly cdRef = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.puids = this.activatedRoute.snapshot.paramMap.get("puids").split(",");
        forkJoin([
            this.produktinstruktionService.getProduktinstruktionDetailsList(this.puids),
            this.produktinstruktionService.getProduktinstruktionBasData(),
            this.produktinstruktionService.getHandelssortiment()
        ]).subscribe((result) => {
            const [
                data,
                basData,
                handelssortiment
            ] = result;
            this.produktinstruktionBasdata = basData;
            this.createSelectedProduktinstruktionViewModels(data);
            this.setFetched(data.valdaProduktinstruktioner[0], data.validationmessages);
            this.produktinstruktionBasdata.handelssortiment = handelssortiment;
            this.dirtyFormService.onDirty(this.form,
                () => this.save(),
                d => this.dirtyCheckCallback(d));
            this.validationContext = ProduktinstruktionerValidation.init(this.aktivProduktdefinition);
            this.validationContext.scope(ValidationScopes.spara).subscribe(v => this.validation = v);
            this.formBinder.attachValidation(this.validationContext.current, this.form);
            this.cdRef.detectChanges();
        });

    }
    save() {
        this.validationContext.validate();
        if (!this.validationContext.hasErrors(ValidationScopes.spara)) {
            this.copyGrundmatris(this.aktivProduktdefinition, this.ovrigaProduktinstruktioner.filter(x => x.headerData.puid !== this.aktivProduktdefinition.headerData.puid));
            const ovrigaProduktintruktioner = this.ovrigaProduktinstruktioner.map(x => x.getDto());
            const index = ovrigaProduktintruktioner.findIndex(x => x.puid === this.aktivProduktdefinition.headerData.puid);
            ovrigaProduktintruktioner.splice(index, 1);

            const dto = new MassediteraProduktdefinitionDto(this.aktivProduktdefinition.getDto(), ovrigaProduktintruktioner);
            this.busyService.addWork(this.produktinstruktionService.saveMassediteringProduktinstruktion(dto)).subscribe(x => {
                this.toastService.add("Ändringar har sparats");
                this.reloadData();
                this.form.markAsPristine();
            });
        }
    }

    reloadData() {
        this.produktinstruktionService.getProduktinstruktionDetailsList(this.puids).subscribe(y => {
            const selectedInstruktion = y.valdaProduktinstruktioner.find(z => z.puid === this.aktivProduktdefinition.headerData.puid);
            this.createSelectedProduktinstruktionViewModels(y);
            this.setFetched(selectedInstruktion, y.validationmessages);
        });
    }

    copyGrundmatris(aktivProduktdefinition: ProduktdefinitionViewModel, ovrigaProduktintruktioner: ProduktdefinitionViewModel[]) {
        ovrigaProduktintruktioner.forEach(x => {
            x.fordelningsmatrisdata.langddistributionMaxavvikelse = aktivProduktdefinition.fordelningsmatrisdata.langddistributionMaxavvikelse;
            x.headerData.maxdiameter = aktivProduktdefinition.headerData.maxdiameter;
            x.headerData.maxlangd = aktivProduktdefinition.headerData.maxlangd;
            x.vardematris.radprisLangdkorrektion = aktivProduktdefinition.vardematris.radprisLangdkorrektion;
            x.vardematris.radprisUtgangsvarde = aktivProduktdefinition.vardematris.radprisUtgangsvarde;
            x.matrisdata.grid.copyRadpriser(aktivProduktdefinition.vardematris.matrisdata.grid);
            x.matrisdata.grid.copyVardematrisProperties(aktivProduktdefinition.vardematris.matrisdata.grid);
        });
    }

    dirtyCheckCallback(result: IDirtyFormResult) {
        if (result.isSkipSave) {
            return;
        } else if (result.isCancel) {
            return;
        } else {
            this.save();
        }
    }

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    canSave(): boolean {
        return this.form.dirty;
    }

    canPublish(): boolean {
        return this.authenticationService.canEdit() && !this.form.dirty && this.ovrigaProduktinstruktioner.every(x => x.hasUtkast);
    }

    publish() {
        this.produktinstruktionService.validatePublishMasseditering(this.puids).subscribe(messages => {
            if (messages.length === 0) {
                this.busyService.addWork(this.produktinstruktionService.publishMasseditering(this.puids)).subscribe(y => {
                    this.reloadData();
                    const filteredList = this.produktinstruktionService.alerts$.value.filter((a) => (a.validationScope & ProduktinstruktionValidationScope.Publicera) <= 0);
                    this.produktinstruktionService.alerts$.next(filteredList);
                    this.toastService.add("Produktinstruktioner är nu publicerade.");
                });
            } else {
                const filteredList =
                    this.produktinstruktionService.alerts$.value.filter((a) => (a.validationScope & ProduktinstruktionValidationScope.Publicera) <= 0);
                this.produktinstruktionService.alerts$.next(filteredList.concat(this.produktinstruktionService.createValidationmessages(messages)));
                this.toastService.add("Kunde inte publicera produktinstruktioner.");
            }
        });
    }

    onSelectedProduktinstruktionChanged(event: ProduktdefinitionViewModel) {
        this.aktivProduktdefinition = event;
    }

    private setFetched(data: IProduktinstruktion, validationmessages: ValidationMessage[]) {
        this.produktinstruktionService.setValidationmessage(validationmessages);
        this.aktivProduktdefinition = new ProduktdefinitionViewModel(
            data,
            this.form,
            !ObjectUtils.isNullOrUndefined(data.utkastProduktdefinition),
            this.canEdit(),
            this.produktinstruktionBasdata,
            true,
            this.ovrigaProduktinstruktioner.filter(x => x.headerData.puid !== data.puid));
    }

    private createSelectedProduktinstruktionViewModels(produktinstruktioner: MassediteraProduktinstruktionerResponseDto) {
        this.ovrigaProduktinstruktioner = [];
        this.produktinstruktionService.setValidationmessage(produktinstruktioner.validationmessages);
        produktinstruktioner.valdaProduktinstruktioner.forEach(produktinstruktion => {
            this.ovrigaProduktinstruktioner
                .push(new ProduktdefinitionViewModel(produktinstruktion,
                    this.form,
                    !ObjectUtils.isNullOrUndefined(produktinstruktion.utkastProduktdefinition),
                    this.canEdit(),
                    this.produktinstruktionBasdata,
                    true));
        });

        this.produktinstruktionService.setValidationmessage(produktinstruktioner.validationmessages);
    }

}
